import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from '../api';
import './OrderPage.css';

export default function OrderPage() {
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    product_url: '', product_description: '', quantity: 1,
    additional_notes: '',
    shipping_address: '', shipping_city: '', shipping_state: '',
    shipping_zip: '', shipping_country: 'United States',
    captcha_answer: ''
  });
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [captcha, setCaptcha] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const fileRef = useRef();

  useEffect(() => { fetchCaptcha(); }, []);

  const fetchCaptcha = async () => {
    try {
      const { data } = await axios.get('/api/captcha/generate');
      setCaptcha(data);
      setForm(f => ({ ...f, captcha_answer: '' }));
    } catch { /* ignore */ }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(e => ({ ...e, [name]: null }));
  };

  const handleImage = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setErrors(e => ({ ...e, image: 'File must be under 10MB' })); return; }
    setImage(file);
    setPreview(URL.createObjectURL(file));
    setErrors(e => ({ ...e, image: null }));
  };

  const removeImage = () => {
    setImage(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = 'Name is required';
    if (!form.customer_email.trim()) e.customer_email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email)) e.customer_email = 'Invalid email format';
    if (!form.shipping_address.trim()) e.shipping_address = 'Address is required';
    if (!form.shipping_city.trim()) e.shipping_city = 'City is required';
    if (!form.shipping_zip.trim()) e.shipping_zip = 'ZIP / Postal code is required';
    if (!form.shipping_country.trim()) e.shipping_country = 'Country is required';
    if (!form.captcha_answer.trim()) e.captcha_answer = 'Please answer the captcha';
    return e;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append('captcha_token', captcha.token);
      if (image) data.append('product_image', image);

      const res = await axios.post('/api/orders/submit', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Submission failed. Please try again.';
      setErrors({ submit: msg });
      fetchCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSuccess(null);
    setForm({
      customer_name: '', customer_email: '', customer_phone: '',
      product_url: '', product_description: '', quantity: 1,
      additional_notes: '',
      shipping_address: '', shipping_city: '', shipping_state: '',
      shipping_zip: '', shipping_country: 'United States',
      captcha_answer: ''
    });
    setImage(null);
    setPreview(null);
    fetchCaptcha();
  };

  if (success) return (
    <div className="order-page">
      <div className="logo-img-wrap">
        <img src="/buykorea-logo.png" alt="BuyKorea Logo" style={{ height: '30px', objectFit: 'contain' }} />
      </div>
      <div className="success-container fade-up">
        <div className="success-icon">✓</div>
        <h2>Order Submitted!</h2>
        <p>Your order has been received. We'll review it and contact you with a quote.</p>
        <div className="success-order-num">
          <span>Order Number</span>
          <strong>{success.order_number}</strong>
        </div>
        <p className="success-email">A confirmation will be sent to your email.</p>
        <div className="success-actions">
          <button className="btn btn-primary" onClick={resetForm}>Submit Another Order</button>
          <Link to="/" className="btn btn-outline">Back to Home</Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="order-page">
      <nav className="order-nav">
        <Link to="/" className="nav-logo"><img src="/buykorea-logo.png" alt="BuyKorea Logo" style={{ height: '36px', objectFit: 'contain' }} /></Link>
        <Link to="/" className="btn btn-outline btn-sm">← Back to Home</Link>
      </nav>

      <div className="order-layout">
        {/* Left side info */}
        <div className="order-info">
          <div className="order-info-inner">
            <p className="section-tag" style={{ color: 'var(--gold)' }}>Request a Purchase</p>
            <h1 className="order-title">Order from<br/><em>Korea</em></h1>
            <p className="order-desc">
              Tell us what you want and we'll source it, check quality, and ship it straight to you.
              We handle the whole process.
            </p>
            <div className="order-tips">
              <h4>💡 Tips for a smooth order:</h4>
              <ul>
                <li>Include a product link or image for accuracy</li>
                <li>Specify size, color, or model clearly</li>
                <li>We'll send you a quote within 24 hours</li>
                <li>Email is required to receive your quote</li>
              </ul>
            </div>
            <div className="order-contact">
              <p>Questions?</p>
              <a href="mailto:sales@buykorea.app">sales@buykorea.app</a>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="order-form-wrap">
          <div className="order-form-card">
            <h2 className="form-title">Order Request Form</h2>

            {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

            <form onSubmit={handleSubmit} noValidate>

              {/* ── Contact Info ── */}
              <div className="form-section-label">Contact Information</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name <span className="required">*</span></label>
                  <input type="text" name="customer_name" value={form.customer_name} onChange={handleChange} placeholder="Your full name" />
                  {errors.customer_name && <span className="error-msg">{errors.customer_name}</span>}
                </div>
                <div className="form-group">
                  <label>Email Address <span className="required">*</span></label>
                  <input type="email" name="customer_email" value={form.customer_email} onChange={handleChange} placeholder="your@email.com" />
                  {errors.customer_email && <span className="error-msg">{errors.customer_email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input type="tel" name="customer_phone" value={form.customer_phone} onChange={handleChange} placeholder="+1 234 567 8900" />
                </div>
                <div className="form-group">
                  <label>Quantity</label>
                  <input type="number" name="quantity" value={form.quantity} onChange={handleChange} min="1" max="999" />
                </div>
              </div>

              {/* ── Shipping Address ── */}
              <div className="form-section-label">Shipping Address</div>

              <div className="form-group">
                <label>Street Address <span className="required">*</span></label>
                <input type="text" name="shipping_address" value={form.shipping_address} onChange={handleChange} placeholder="123 Main St, Apt 4B" />
                {errors.shipping_address && <span className="error-msg">{errors.shipping_address}</span>}
              </div>

              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>City <span className="required">*</span></label>
                  <input type="text" name="shipping_city" value={form.shipping_city} onChange={handleChange} placeholder="New York" />
                  {errors.shipping_city && <span className="error-msg">{errors.shipping_city}</span>}
                </div>
                <div className="form-group">
                  <label>State / Province</label>
                  <input type="text" name="shipping_state" value={form.shipping_state} onChange={handleChange} placeholder="NY" />
                </div>
                <div className="form-group">
                  <label>ZIP / Postal Code <span className="required">*</span></label>
                  <input type="text" name="shipping_zip" value={form.shipping_zip} onChange={handleChange} placeholder="10001" />
                  {errors.shipping_zip && <span className="error-msg">{errors.shipping_zip}</span>}
                </div>
              </div>

              <div className="form-group">
                <label>Country <span className="required">*</span></label>
                <select name="shipping_country" value={form.shipping_country} onChange={handleChange} className="form-select">
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Japan">Japan</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Other">Other</option>
                </select>
                {errors.shipping_country && <span className="error-msg">{errors.shipping_country}</span>}
              </div>

              {/* ── Product Info ── */}
              <div className="form-section-label">Product Details</div>

              <div className="form-group">
                <label>Product URL</label>
                <input type="url" name="product_url" value={form.product_url} onChange={handleChange} placeholder="https://..." />
                <span className="hint">Paste a link to the product page (Naver, Coupang, Kakao, etc.)</span>
              </div>

              <div className="form-group">
                <label>Product Description</label>
                <textarea name="product_description" value={form.product_description} onChange={handleChange} placeholder="Describe the item: name, brand, color, size, model..." rows={4} />
              </div>

              {/* Image Upload */}
              <div className="form-group">
                <label>Product Photo</label>
                <div className={`upload-zone ${preview ? 'has-preview' : ''}`} onClick={() => !preview && fileRef.current?.click()}>
                  {preview ? (
                    <div className="preview-wrap">
                      <img src={preview} alt="Preview" className="img-preview" />
                      <button type="button" className="remove-img" onClick={e => { e.stopPropagation(); removeImage(); }}>✕</button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">📷</div>
                      <p>Click to upload product image</p>
                      <span>JPG, PNG, GIF, WEBP — Max 10MB</span>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} style={{ display: 'none' }} />
                </div>
                {errors.image && <span className="error-msg">{errors.image}</span>}
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea name="additional_notes" value={form.additional_notes} onChange={handleChange} placeholder="Shipping preferences, special requests, deadlines..." rows={3} />
              </div>

              {/* Captcha */}
              <div className="form-group captcha-group">
                <label>Security Check <span className="required">*</span></label>
                <div className="captcha-box">
                  <div className="captcha-question">
                    <span className="captcha-icon">🔐</span>
                    <span className="captcha-text">{captcha ? captcha.question : 'Loading...'}</span>
                  </div>
                  <div className="captcha-input-wrap">
                    <input type="number" name="captcha_answer" value={form.captcha_answer} onChange={handleChange} placeholder="Answer" className="captcha-input" />
                    <button type="button" className="captcha-refresh" onClick={fetchCaptcha} title="Get new question">↻</button>
                  </div>
                </div>
                {errors.captcha_answer && <span className="error-msg">{errors.captcha_answer}</span>}
              </div>

              <button type="submit" className="btn btn-primary submit-btn" disabled={loading}>
                {loading ? <><span className="spinner"></span> Submitting...</> : <>Submit Order Request →</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}